import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '@wordpress/i18n';
import CptTable from './CptTable';
import { spaceColumns } from './CptTable/columns/spaces';
import { apiFetch, fetchTableRows } from '../scripts/api';
import { splitValues, downloadCsv } from '../scripts/exportCsv';

const taxNames = ( c ) => c?.nodes?.map( ( n ) => n.name ).join( '; ' ) ?? '';
const postTitles = ( c ) =>
	c?.nodes?.map( ( n ) => n.title ).join( '; ' ) ?? '';

function exportSpacesToCSV( rows ) {
	const headers = [
		__( 'Space', 'anacrm' ),
		__( 'Territory', 'anacrm' ),
		__( 'Municipality', 'anacrm' ),
		__( 'Email', 'anacrm' ),
		__( 'Phone', 'anacrm' ),
		__( 'Web', 'anacrm' ),
		__( 'Address', 'anacrm' ),
		__( 'Space type', 'anacrm' ),
		__( 'Governance', 'anacrm' ),
		__( 'Capacity', 'anacrm' ),
		__( 'Services', 'anacrm' ),
	];
	const dataRows = rows.map( ( r ) => [
		r.title ?? '',
		taxNames( r.territories ),
		taxNames( r.cities ),
		r.contactMail ?? '',
		r.contactPhone ?? '',
		r.contactWebsite ?? '',
		r.address ?? '',
		taxNames( r.spaceTypes ),
		r.governance ?? '',
		Array.isArray( r.capacityList ) ? r.capacityList.join( '; ' ) : '',
		postTitles( r.service ),
	] );
	downloadCsv(
		headers,
		dataRows,
		`spaces-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}

export default function SpaceTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ emailList, setEmailList ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	useEffect( () => {
		fetchTableRows( 'space' )
			.then( ( nodes ) => setData( nodes ) )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/space/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not delete the space:', 'anacrm' ) + err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-space', handler );
		return () =>
			document.removeEventListener( 'anacrm:share-space', handler );
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	function handleEmailExport( rows ) {
		const emails = rows
			.map( ( r ) => splitValues( r.contactMail )[ 0 ] )
			.filter( Boolean )
			.join( ', ' );
		setEmailList( emails );
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() => spaceColumns( { onDelete: handleDelete } ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'territories',
			label: __( 'Territory', 'anacrm' ),
			getTerms: ( row ) =>
				row.territories?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'cities',
			label: __( 'Municipality', 'anacrm' ),
			getTerms: ( row ) =>
				row.cities?.nodes?.map( ( n ) => n.name ) ?? [],
			allowedValues: window.anacrm?.allowedArchiveCities ?? null,
			dependsOnColumnId: 'territories',
			relationMap: window.anacrm?.cityTerritoryMap ?? {},
			disabledWhenValues:
				window.anacrm?.cityFilterDisabledTerritories ?? [],
		},
		{
			columnId: 'spaceTypes',
			label: __( 'Space type', 'anacrm' ),
			getTerms: ( row ) =>
				row.spaceTypes?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'governance',
			label: __( 'Governance', 'anacrm' ),
			getTerms: ( row ) => ( row.governance ? [ row.governance ] : [] ),
		},
		{
			columnId: 'service',
			label: __( 'Service', 'anacrm' ),
			getTerms: ( row ) =>
				row.service?.nodes?.map( ( n ) => n.title ) ?? [],
		},
	];

	const numericFilters = [
		{ columnId: 'capacity', label: __( 'Min. capacity', 'anacrm' ) },
	];

	const emailContainer = document.getElementById(
		'anacrm-email-export-space'
	);
	const shareContainer = document.getElementById( 'anacrm-share-space' );

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				numericFilters={ numericFilters }
				exportEvent="anacrm:export-space"
				onExportRows={ exportSpacesToCSV }
				emailExportEvent="anacrm:export-emails-space"
				onEmailExportRows={ handleEmailExport }
				searchPlaceholder={ __( 'Search spaces…', 'anacrm' ) }
				fitWidth
			/>
			{ emailList !== null &&
				emailContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<span>{ emailList }</span>
						<button
							onClick={ () => setEmailList( null ) }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer"
						>
							&times;
						</button>
					</div>,
					emailContainer
				) }
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
